import {
  type APIFieldAttributes,
  APIReferenceAttributes,
  APIWorkspaceAttributes,
  ArdoqId,
} from '@ardoq/api-types';
import { DIFF_TYPE, VIRTUAL_MODEL_ID } from '@ardoq/global-consts';
import { DiffType, ModelEntityTypePair } from '@ardoq/data-model';

/**
 * Function to normalize the reference type id to string
 */
export const getTypeId = (reference: APIReferenceAttributes): string => {
  return String(reference.type);
};

export const getEntityTypePair = (
  reference: APIReferenceAttributes
): ModelEntityTypePair => {
  return {
    typeId: getTypeId(reference),
    modelId: reference.model,
  };
};

export const isValidRefType = (refType?: string | number): boolean =>
  isFinite(parseInt(refType as string, 10));

export const flipSourceAndTarget = (
  attributes: APIReferenceAttributes,
  targetWorkspace: APIWorkspaceAttributes,
  wsFlipRefType: number
): APIReferenceAttributes => {
  const flipped: APIReferenceAttributes = {
    ...attributes,
    source: attributes.target,
    target: attributes.source,
  };

  if (attributes.rootWorkspace !== attributes.targetWorkspace) {
    flipped.rootWorkspace = attributes.targetWorkspace;
    flipped.targetWorkspace = attributes.rootWorkspace;
    flipped.type = wsFlipRefType;
    flipped.model = targetWorkspace.componentModel;
  }

  return flipped;
};

export const hasURLFields = (
  reference: APIReferenceAttributes,
  urlFields: APIFieldAttributes[]
): boolean => urlFields.some(field => Boolean(reference[field.name]));

export const getVisualDiffType = (
  reference: APIReferenceAttributes
): DiffType => reference[DIFF_TYPE] || DiffType.NONE;

export const getGlobalTypeId = (reference: APIReferenceAttributes): string =>
  `${reference.rootWorkspace}-${reference.type}`;

export const isVirtualReference = (ref: APIReferenceAttributes): boolean =>
  ref.model === VIRTUAL_MODEL_ID;

export const getCssClassNames = (
  reference: APIReferenceAttributes,
  sanitizedFilterColor: string | undefined
) => {
  const type = reference.type;
  const modelId = reference.model;

  return `integration m${modelId} nr${type} id_${reference._id} ${sanitizedFilterColor}`;
};

export const isInWorkspace = (
  reference: APIReferenceAttributes,
  workspaceId: ArdoqId
): boolean =>
  reference.rootWorkspace === workspaceId ||
  reference.targetWorkspace === workspaceId;

export const isForComponent = (
  reference: APIReferenceAttributes,
  componentId: ArdoqId
) => reference.source === componentId || reference.target === componentId;
